/* eslint-disable curly */
/* eslint-disable arrow-body-style */
import { ChangeDetectorRef, Component, Input, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { first, map } from "rxjs/operators";
import { ProductSource } from "src/app/data-models/wl-account-setup/wl-config-data.model";
import { WhiteLabelConfigurationService } from "src/app/services/white-labels/white-label-configuration.service";
import { Exceptions } from "src/app/utils/exceptions";
import { setupFullscreenPanelObserver } from "src/app/utils/intiaro-fullscreen-observer";
import { injectNonVisualThumbnail } from "src/app/utils/intiaro-non-visual-thumbnail";
import { ProductConfigConfigurator360Component } from "../product-config-configurator360/product-config-configurator360.component";

declare var IntiaroAPI: any;

@Component({
  selector: "app-product-design-configurator360",
  templateUrl: "./product-design-configurator360.component.html",
  styleUrls: ["./product-design-configurator360.component.scss"]
})
export class ProductDesignConfigurator360Component implements OnInit, AfterViewInit, OnDestroy {

  @Output() configurator360ReadyEvent: EventEmitter<boolean> = new EventEmitter();

  @Input() configurationId!: string;
  @Input() brandId!: string;
  @Input() tearsheetTemplateId!: string;
  @Input() showMenu:boolean = true;
  @Input() showRotateHint:boolean = false;
  @Input() isFlexibleSliderShapePreference?: boolean = false; // TODO: brak odpowiednika w v3.0
  @Input() isVisual?: boolean = true;
  @Input() showArButton?: boolean = false;
  @Input() showNavigationButtons?: boolean = false;
  @Input() context?: string;
  @Input() productId!: string;
  @Input() thumbnailUrl?: string;

  @ViewChild("element360", { read: ElementRef })
  private element360!: ElementRef;

  hiddenInactiveChoices: boolean = false;
  wlSlug: string = "";

  public static readonly defaultConfiguratorId = "product-design-configurator";

  get defaultConfiguratorId() {
    return ProductDesignConfigurator360Component.defaultConfiguratorId;
  }

  public isConfigurator360Loaded:Subject<boolean> = new Subject();
  public intiaroApiInstance: any = null;
  private fullscreenPanelObserver: MutationObserver | null = null;
  private productSourceSubscription?: Subscription;

  constructor(
    public changeDetector: ChangeDetectorRef,
    private wlConfigurationService: WhiteLabelConfigurationService
  ) {}

  ngOnInit(): void {
    this.wlConfigurationService.whiteLabelSetupConfiguration.subscribe((wlConfig) => {
      this.wlSlug = wlConfig.slug;
      this.hiddenInactiveChoices = wlConfig.configuratorInactiveChoicesVisible ?? false;
    });
    this.changeDetector.detectChanges();
  }

  ngAfterViewInit(): void {
    // The product source picks the configurator's data, so the configurator waits for the white label configuration.
    this.productSourceSubscription = this.wlConfigurationService.whiteLabelSetupConfiguration.pipe(
      map((wlConfig) => wlConfig?.productSource),
      first((productSource): productSource is ProductSource => productSource !== undefined)
    ).subscribe((productSource) => this.createIntiaroInstance(productSource));
  }

  private async createIntiaroInstance(productSource: ProductSource): Promise<void> {
    this.changeDetector.detectChanges();

    this.element360.nativeElement.id = this.defaultConfiguratorId;
    this.element360.nativeElement.style = "line-height: normal;";

    if (!this.showMenu) {
      this.element360.nativeElement.classList.add("intiaro360player");
      this.element360.nativeElement.style = "max-height: calc(100% - 30px)";
    }

    this.intiaroApiInstance = new IntiaroAPI({
      configurationId: this.configurationId,
      brandId: this.brandId,
      domainTemplate: productSource,
      environment: productSource,
      ...(this.context ? { context: this.context } : {}),
      // v3.0 reads the tearsheet template here, not in menuConfiguration: the PDF service is
      // built from the IntiaroAPI constructor params, so the id also reaches the createViewer
      // path used by the design view. Sending it in menuConfiguration was a no-op.
      ...(this.tearsheetTemplateId ? { tearSheetTemplateId: this.tearsheetTemplateId } : {}),
    });

    const viewerConfiguration: any = {
      siloShotButton: false,
      ...(this.showArButton === false ? { arButton: false } : {}),
      ...(this.showNavigationButtons === false || !this.isVisual ? { navigationButtons: false } : {}),
    };

    if (!this.showMenu) {
      viewerConfiguration.measurementsButton = false;
    }

    if (!this.isVisual) {
      viewerConfiguration.measurementsButton = false;
      viewerConfiguration.siloShotButton = false;
      viewerConfiguration.fullscreenButton = false;
    }

    if (Exceptions.isWindowDraperyProduct(this.wlSlug, this.productId)) {
      viewerConfiguration.allowedAngles = JSON.parse(ProductConfigConfigurator360Component.ALLOWED_ANGLES);
      viewerConfiguration.arButton = false;
    }

    if (this.showMenu) {
      await this.intiaroApiInstance.createConfigurator({
        parentElement: this.element360.nativeElement,
        viewerConfiguration,
        menuConfiguration: {
          inactiveChoicesBehaviour: this.hiddenInactiveChoices ? "hide" : "gray-out",
          // v3.0 (FFY-4550): hide attributes that offer only one choice. Bundles without
          // support for this option ignore the key, so it is safe to send before the release.
          singleChoiceAttributes: "hidden",
        },
      });
      this.fullscreenPanelObserver = setupFullscreenPanelObserver(this.element360.nativeElement);
    } else {
      await this.intiaroApiInstance.createViewer({
        parentElement: this.element360.nativeElement,
        viewerConfiguration,
      });
    }

    if (!this.isVisual && this.thumbnailUrl) {
      injectNonVisualThumbnail(this.element360.nativeElement, this.thumbnailUrl);
    }

    this.intiaro360Ready();
  }

  ngOnDestroy(): void {
    this.productSourceSubscription?.unsubscribe();
    this.fullscreenPanelObserver?.disconnect();
  }

  private intiaro360Ready(): void {
    this.isConfigurator360Loaded.next(true);
    this.configurator360ReadyEvent.emit(true);
  }
}
